import React, { useEffect } from 'react';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import {FormInput} from '../../../../../../components/shared/form-controls/form-input';
import {FormCheckbox} from '../../../../../../components/shared/form-controls/form-checkbox';
import {useAppDispatch, useAppSelector} from '../../../../../../hooks/hooks';
import {addProjectType, filterProjectType} from '../../../../../../features/types';
import {TiminatorType} from '../../../../../../features/types/types';
import { useIntl } from 'react-intl';
import { Lx } from '../../../../../../i18n/consts';
import BaseForm from '../../../../../../components/shared/form-controls/base-form';
import { useNavigate } from 'react-router-dom';
import { NAVIGATION_ROUTES } from '../../../../../../routes/NavigationRoute';
import { canManageProjectTypes } from '../../../../../../features/profile/entityPermissions';

interface CreateForm {
    name: string;
    isDefault: boolean;
    isActive: boolean;
}

export default function ProjectTypeCreateForm() {
    const dispatch = useAppDispatch();
    const intl = useIntl();

    const navigate = useNavigate();
    const profile = useAppSelector(store => store.profile);
    const canEditProjectTypes = canManageProjectTypes(profile);

    useEffect(() => {
        if (!canEditProjectTypes) {
            navigate(NAVIGATION_ROUTES.PROJECT_TYPES);
        }
    }, [canEditProjectTypes, navigate]);

    if (!canEditProjectTypes) {
        return null;
    }

    const initialValue: CreateForm = {
        name: '',
        isDefault: false,
        isActive: true,
    };

    const validateName = async (name: string): Promise<boolean> => {
        name = name.toLowerCase();
        if (name === '') {
            return Promise.resolve(false);
        }
        return await dispatch(filterProjectType(name)).unwrap().then((values) => {
            return values.length < 1 || values.some(value => value.name.toLowerCase() !== name);
        });
    }

    const validationScheme: Yup.Schema<CreateForm> = Yup.object({
        name: Yup.string()
            .max(45, 'Must be 45 characters or less')
            .required(intl.formatMessage({ id: Lx.Form.NAME_REQUIRED_FORMAT }, { s: intl.formatMessage({ id: Lx.General.PROJECT_TYPE }) }))
            .test(
                'type-name-backend-validation',
                'project type name is taken',
                async (name) => { return await validateName(name)}),
        isDefault: Yup.boolean().required(),
        isActive: Yup.boolean().required(),
    })

    const save = async (values: CreateForm) => {
        const type: TiminatorType = {
            id: 0,
            name: values.name,
            active: values.isActive,
            default: values.isDefault,
        }
        return dispatch(addProjectType(type));
    }

    return <Formik
        initialValues={initialValue}
        validationSchema={validationScheme}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={save}>
        <Form>
            <BaseForm contentGap={24}
                formType='create'
                formHeader='create_project_type'
                savingTitle='Project type' 
                onClose={() => navigate(NAVIGATION_ROUTES.PROJECT_TYPES)} 
                onAfterSave={() => navigate(NAVIGATION_ROUTES.PROJECT_TYPES)}>
                <FormInput name='name' label='Name' placeholder='Project type' limited required={false} />
                <fieldset>
                    <FormCheckbox name='isDefault' label='Default' />
                    <FormCheckbox name='isActive' label='Active'/>
                </fieldset>
            </BaseForm>
        </Form>
    </Formik>
}
